// Generated by OrchestKit Claude Plugin
// Created: 2026-09-19

import type { Colonne } from './table.tsx';

/**
 * `jspdf`/`jspdf-autotable` pèsent plusieurs centaines de kilo-octets — trop
 * pour les faire charger à CHAQUE page, sur une connexion béninoise lente,
 * pour une action qu'on utilise occasionnellement. L'import dynamique les
 * place dans un fichier à part, chargé une seule fois, au premier clic sur
 * « Imprimer ».
 */
export async function chargerPdf(): Promise<{
  jsPDF: typeof import('jspdf').jsPDF;
  autoTable: typeof import('jspdf-autotable').autoTable;
}> {
  const [{ jsPDF }, { autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);
  return { jsPDF, autoTable };
}

/**
 * Export PDF d'un tableau — écrans de liste.
 *
 * Réutilise les MÊMES `colonnes` que `<DataTable>`, via leur accesseur
 * `texte` (ajouté à `Colonne` pour cet usage précis) : une colonne sans
 * `texte` — un `Badge` ou une action, par exemple — est simplement absente
 * du PDF, plutôt que d'y montrer `[object Object]`.
 *
 * Plus de six colonnes bascule en paysage : au-delà, le portrait tasse trop
 * les montants pour rester lisible imprimé.
 */
export async function exporterTableauPdf<T>({
  titre,
  sousTitre,
  colonnes,
  lignes,
  nomFichier,
}: {
  titre: string;
  sousTitre?: string;
  colonnes: Colonne<T>[];
  lignes: T[];
  nomFichier: string;
}): Promise<{ blob: Blob; nomFichier: string }> {
  const { jsPDF, autoTable } = await chargerPdf();
  const avecTexte = colonnes.filter((colonne) => colonne.texte !== undefined);

  const doc = new jsPDF({
    orientation: avecTexte.length > 6 ? 'landscape' : 'portrait',
    unit: 'mm',
  });

  doc.setFontSize(14);
  doc.text(titre, 14, 15);

  if (sousTitre !== undefined) {
    doc.setFontSize(10);
    doc.setTextColor(120, 130, 145);
    doc.text(sousTitre, 14, 21);
  }

  autoTable(doc, {
    startY: sousTitre === undefined ? 20 : 26,
    head: [avecTexte.map((colonne) => colonne.entete)],
    body: lignes.map((ligne) => avecTexte.map((colonne) => colonne.texte?.(ligne) ?? '')),
    styles: { fontSize: 9, cellPadding: 2.5 },
    // Bleu de marque — cohérent avec l'accent de l'écran (tokens.css).
    headStyles: { fillColor: [0, 113, 189] },
    columnStyles: Object.fromEntries(
      avecTexte
        .map((colonne, index) => [index, colonne.numerique === true ? { halign: 'right' as const } : null])
        .filter((entree): entree is [number, { halign: 'right' }] => entree[1] !== null),
    ),
    didDrawPage: () => {
      const page = doc.getCurrentPageInfo().pageNumber;
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Généré le ${new Date().toLocaleString('fr-FR', { timeZone: 'Africa/Porto-Novo' })} — page ${String(page)}`,
        14,
        doc.internal.pageSize.getHeight() - 8,
      );
    },
  });

  return { blob: doc.output('blob'), nomFichier };
}

